'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import { Ship, Bell, Shield, Globe } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const features = [
  { id: 'feat-track', icon: Ship, text: 'Track BLs across 26 carriers in real-time' },
  { id: 'feat-alert', icon: Bell, text: 'SMS & email alerts when status changes' },
  { id: 'feat-secure', icon: Shield, text: 'Secure dashboard for all your shipments' },
  { id: 'feat-india', icon: Globe, text: 'All major Indian & international carriers' },
];

export default function AuthScreen() {
  const [tab, setTab] = useState<'login' | 'register'>('login');

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 gradient-ocean flex-col justify-between p-10 xl:p-14">
        <Link href="/" className="flex items-center gap-3">
          <AppLogo size={40} />
          <span className="text-2xl font-bold text-white tracking-tight">EzyTrack</span>
        </Link>

        <div className="flex flex-col gap-6">
          <div>
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-3">
              India's Fastest<br />BL Tracking Platform
            </h2>
            <p className="text-blue-100 text-base xl:text-lg leading-relaxed">
              Track your shipments, set custom alerts, and never miss a consignment arrival again.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {features?.map((f) => {
              const Icon = f?.icon;
              return (
                <div key={f?.id} className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-3">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                    <Icon size={16} className="text-white" />
                  </div>
                  <p className="text-sm text-white font-medium">{f?.text}</p>
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-blue-200 text-sm">
          Trusted by 2,400+ freight forwarders across India
        </p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 py-8 bg-background overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <AppLogo size={36} />
            <span className="text-xl font-bold text-primary">EzyTrack</span>
          </div>

          {/* Tabs */}
          <div className="flex rounded-xl bg-muted p-1 mb-6">
            <button
              onClick={() => setTab('login')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                tab === 'login' ?'bg-card text-primary shadow-card' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setTab('register')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                tab === 'register' ?'bg-card text-primary shadow-card' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Register
            </button>
          </div>

          {tab === 'login' ? (
            <LoginForm onSwitchToRegister={() => setTab('register')} />
          ) : (
            <RegisterForm onSwitchToLogin={() => setTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}